<template>
  <div :class="[$style.dropdown, $style.buttonContainer]">
    <button
      :class="[$style.printButton, $style.exportButton]"
      :disabled="!data.length"
    >
      {{ $t('pages.exportTo') }}
    </button>
    <div
      v-if="data.length"
      :class="$style.dropdownContent"
    >
      <div
        v-for="option in exportOptions"
        :key="option.name"
        :v-bind:value="option"
        :class="$style.item"
        @click="exportTable(option.name)"
      >
        <div :class="$style.dropdownItem">
          {{ option.name }}
        </div>

        <div
          v-if="option.children"
          :class="$style.dropdownNestedContent"
        >
          <div
            v-for="child in option.children"
            :key="child.name"
            :class="$style.dropdownNestedItem"
            :v-bind:value="child"
            @click="exportTable(child.name)"
          >
            {{ child.name }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import html2pdf from 'html-to-pdf-js';
  import { logTypes } from '@/assets/scripts/dictionaries';
  import * as dayjs from 'dayjs';
  import '@/components/common/logs/mixins/Roboto-Regular-normal';
  import JsPdf from 'jspdf';
  import autoTable from 'jspdf-autotable';
  import { getClipboardFileLink } from '@/api/methods/computers';
  import { getScreenshotUrl } from '@/helpers/methods';
  import { dateTime, duration } from '@/helpers/filters';
  import { type as typeClipboard } from '@/components/pages/computers/log/clipboard/utils';
  import { action as actionComputer } from '@/components/pages/computers/log/computer/utils';
  import { type as typeEmail } from '@/components/pages/computers/log/email-clients/utils';
  import {
    driveType as driveTypeExternal,
    action as actionExternal,
  } from '@/components/pages/computers/log/external-drives/utils';
  import { action as actionFiles } from '@/components/pages/computers/log/files/utils';
  import { action as actionHardware } from '@/components/pages/computers/log/hardware-changes/utils';
  import { action as actionInstalled } from '@/components/pages/computers/log/installed-programs/utils';
  import { action as actionNetwork } from '@/components/pages/computers/log/network-accesses/utils';
  import { status as statusPrograms } from '@/components/pages/computers/log/programs/utils';
  import {
    source as sourceShadow,
    action as actionShadow,
    available as availableShadow,
  } from '@/components/pages/computers/log/shadow-copyings/utils';
  import { makeLink } from '@/components/pages/computers/log/visited-sites/utils';
  import { FORMAT_EXPORT, ROUTE_NAMES_SERVER_EXPORT_PDF } from '@/views/reports/const/const';
  import { getPdf } from '@/api/methods/pdf';

  export default {
    filters: {
      typeClipboard,
      actionComputer,
      typeEmail,
      driveTypeExternal,
      actionExternal,
      actionFiles,
      actionHardware,
      actionInstalled,
      actionNetwork,
      statusPrograms,
      sourceShadow,
      actionShadow,
      availableShadow,
      makeLink,
    },
    props: {
      isPdfPage: {
        type: Boolean,
        default: false,
      },
      selectedComputers: {
        type: Array,
        default: () => [],
      },
      selectedLogs: {
        type: Array,
        default: () => [],
      },
    },
    data: () => ({
      exportOptions: [{ name: 'pdf' }, { name: 'xls' }, { name: 'csv' }],
    }),
    computed: {
      data() {
        return this.$store.state.logs.printData.data;
      },
      columns() {
        return this.$store.state.logs.printData.columns;
      },
      pdfName() {
        let logname;
        let filename;

        const { params, name } = this.$route;
        const { log } = params;
        if (name !== 'diagnostics' && name !== 'advanced' && name !== 'notifications') {
          const targetLog = logTypes.find((logType) => logType.slug === log);
          logname = targetLog.name;
          filename = log;
        } else if (name === 'advanced') {
          logname = this.$t('pages.advancedMode');
          filename = name;
        } else if (name === 'notifications') {
          logname = this.$t('pages.notifications');
          filename = name;
        } else if (name === 'diagnostics') {
          logname = this.$t('navigation.control.diagnostics.name');
          filename = name;
        }

        return {
          logname,
          filename,
        };
      },
    },
    watch: {
      $route(route) {
        const { query, params, name } = route;
        const { view } = query;
        const { log } = params;

        if (view === 'grid' && log === 'screenshots') {
          this.exportOptions = [
            {
              name: 'pdf',
              children: [
                { name: this.$t('pages.portraitOrientation') },
                { name: this.$t('pages.landscapeOrientation') },
              ],
            },
          ];
        } else if (view === 'groupped' && (log === 'messengers' || name === 'advanced' || name === 'notifications')) {
          this.exportOptions = [{ name: 'pdf' }];
        } else {
          this.exportOptions = [{ name: 'pdf' }, { name: 'xls' }, { name: 'csv' }];
        }
      },
    },

    created() {
      const { query, params } = this.$route;
      const { view } = query;
      const { log } = params;
      if (view === 'grid' && log === 'screenshots') {
        this.exportOptions = [
          {
            name: 'pdf',
            children: [{ name: this.$t('pages.portraitOrientation') }, { name: this.$t('pages.landscapeOrientation') }],
          },
        ];
      }
    },

    methods: {
      fieldFunctions(data, field) {
        const { log } = this.$route.params;
        const fieldNames = {
          time: (item) => dateTime(item.date),
          duration: (item) => duration(item.duration),
          type: (item) => typeClipboard(item.type),
          driveType: (item) => driveTypeExternal(item.driveType),
          action: (item) => {
            if (log === 'computer') return actionComputer(item.action);
            if (log === 'external-drives' || log === 'network-files') return actionExternal(item.action);
            if (log === 'files') return actionFiles(item.action);
            if (log === 'hardware-changes') return actionHardware(item.action);
            if (log === 'installed-programs') return actionInstalled(item.action);
            if (log === 'network-accesses' || log === 'network-files') return actionNetwork(item.action);
            if (log === 'shadow-copyings') return actionShadow(item.action);
            return item.action;
          },
          status: (item) => statusPrograms(item.status),
          source: (item) => sourceShadow(item.source),
          available: (item) => availableShadow(item.available),
          link: (item) => makeLink(item.link),
          subject: (item) => `[${typeEmail(item.type)}] ${item.subject}`,
        };

        return fieldNames[field] ? fieldNames[field](data) : data[field];
      },

      getScreenshotUrl,
      getClipboardFileLink,
      async getScreenshotBase64(url) {
        try {
          const response = await fetch(url);
          const blob = await response.blob();
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
        } catch (error) {
          console.error('Ошибка загрузки base64:', error);
          return '';
        }
      },

      async generatePdf(doc, options) {
        try {
          const { query } = this.$route;

          doc.addFont('Roboto-Regular-normal.ttf', 'Roboto-Regular', 'normal');
          doc.setFont('Roboto-Regular');
          doc.setFontSize(12);

          const dateFrom = new Date(query.dateFrom);
          const dateTo = new Date(query.dateTo);
          const dateFromFormatted = dayjs(dateFrom).format('DD.MM.YY');
          const dateToFormatted = dayjs(dateTo).format('DD.MM.YY');

          doc.text(`${this.pdfName.logname} c: ${dateFromFormatted} по: ${dateToFormatted}`, 15, 10);

          autoTable(doc, {
            ...options,
          });

          doc.save(`${this.pdfName.filename} ${dateFromFormatted}-${dateToFormatted}.pdf`);
        } catch (error) {
          console.error(error);
        }
      },

      async getPdfScreenshots(isAlbum) {
        try {
          const orientation = isAlbum ? 'landscape' : 'portrait';
          const doc = new JsPdf({ orientation });

          const promises = this.data.map(async (item) => {
            if (!item.path) return null;
            try {
              const imageUrl = this.getScreenshotUrl(item.path);
              const img = await new Promise((resolve, reject) => {
                const image = new Image();
                image.onload = () => resolve(image);
                image.onerror = reject;
                image.src = imageUrl;
              });

              const base64Img = await this.getScreenshotBase64(imageUrl);
              return [base64Img, img.width / img.height];
            } catch (error) {
              console.warn('Screenshot load failed:', item.path, error);
              return null;
            }
          });

          const screenshots = await Promise.all(promises);

          const body = [];
          this.data.forEach((item) => {
            const { date, caption } = item;
            let rowData = '';
            if (date) rowData = `${dateTime(date)} `;
            if (caption) rowData += `${caption}`;
            body.push([rowData]);
          });

          const didDrawCell = (data) => {
            const { cell, row } = data;
            const { x, y } = cell;

            screenshots.forEach((screenshot, indexImg) => {
              if (!screenshot) return;

              const [base64Img, prop] = screenshot;
              if (data.section === 'body' && row.index === indexImg) {
                const widthImg = cell.width;
                const heightImg = widthImg / prop;
                doc.addImage(base64Img, 'JPEG', x, y + 7, widthImg, heightImg);
              }
            });
          };

          const options = {
            body,
            didDrawCell,
            styles: {
              font: 'Roboto-Regular',
              fontStyle: 'normal',
              fontSize: '8',
              minCellHeight: isAlbum ? '180' : '133',
            },
            columnStyles: { 0: { fillColor: [255, 255, 255] } },
          };
          await this.generatePdf(doc, options);
        } catch (error) {
          console.error(error);
        }
      },
      async getPdfTable() {
        const doc = new JsPdf();
        const head = this.columns.map((item) => item.title || item.name);

        const body = this.data.map((item) => {
          const rowData = [];
          this.columns.forEach(({ field }) => {
            const result = this.fieldFunctions(item, field);
            rowData.push(result);
          });
          return rowData;
        });

        const options = {
          head: [head],
          body,
          showHead: 'firstPage',
          startY: 15,
          styles: {
            font: 'Roboto-Regular',
            fontStyle: 'normal',
            minCellWidth: '30',
          },
          columnWidth: 'wrap',
        };
        await this.generatePdf(doc, options);
      },
      async getPdfClipboard() {
        const doc = new JsPdf();
        const head = this.columns.map((item) => item.title || item.name);

        const { computer } = this.$route.params;

        const promises = this.data.flatMap(async (item) => {
          const { id, type, filepath, text } = item;
          const rowData = [];
          this.columns.forEach(({ field }) => {
            const result = this.fieldFunctions(item, field);
            rowData.push(result);
          });
          let content = '';
          let img64;
          let heightImg;

          if (type === 2) {
            const imageUrl = this.getClipboardFileLink(parseInt(computer, 10), id);

            const imgPromise = new Promise((resolve) => {
              const img = new Image();
              img.onload = () => resolve(img);
              img.src = imageUrl;
            });

            const img = await imgPromise;
            img64 = await this.getScreenshotBase64(imageUrl);
            const prop = img.width / img.height;
            heightImg = 177 / prop;
            content = `${this.$t('pages.screenshot')}:`;
          } else if (type === 0) {
            content = `${this.$t('pages.text')}: ${text}`;
          } else if (type === 1 || type === 3) {
            content = `${this.$t('pages.file')}: ${filepath}`;
          }

          const payload = [
            {
              type,
              img64,
              heightImg,
              content,
              colSpan: 4,
              styles: {
                fillColor: [255, 255, 255],
              },
            },
          ];

          const data = [rowData, payload];
          return data;
        });

        const promiseData = await Promise.all(promises);
        const body = promiseData.flat();

        const processedRows = [];

        const didDrawCell = (data) => {
          const { cell, row, table } = data;
          const { x, y } = cell;
          if (data.section === 'body' && processedRows.indexOf(row.index) === -1) {
            processedRows.push(row.index);
            const currentRowHeight = row.height;

            const { type, img64, heightImg } = row.raw[0];

            if (type === 2) {
              row.height = currentRowHeight + heightImg + 7;
              doc.addImage(img64, 'JPEG', x + 2, y + currentRowHeight + 2, 177, heightImg);
            }

            const heightPage = 297;
            const nextRow = table.body[row.index + 1];
            const screenshotRow = table.body[row.index + 2];

            if (nextRow && nextRow.raw[3] === this.$t('pages.screenshot')) {
              const nextRowY = cell.y + row.height;
              const heightScreenshot = screenshotRow.raw[0].heightImg;
              const nextHeightRow = nextRow.height + heightScreenshot + 17;
              const diff = heightPage - nextRowY;
              if (diff < nextHeightRow) {
                row.height += 200;
              }
            }
          }
        };

        const options = {
          head: [head],
          body,
          didDrawCell,
          showHead: 'firstPage',
          startY: 15,
          styles: {
            font: 'Roboto-Regular',
            fontStyle: 'normal',
            minCellWidth: '30',
            overflow: 'linebreak',
          },
          alternateRowStyles: { fillColor: [242, 242, 242] },
          columnWidth: 'wrap',
        };
        await this.generatePdf(doc, options);
      },

      async pdfExport(isAlbum) {
        const { query, params } = this.$route;
        const { view } = query;
        const { log } = params;
        try {
          this.$store.commit('loader/setIsLoaderNeeded', true);
          if (log === 'screenshots' && view === 'grid') {
            await this.getPdfScreenshots(isAlbum);
          } else if (log === 'clipboard') {
            await this.getPdfClipboard();
          } else {
            await this.getPdfTable();
          }
        } catch (error) {
          console.error(error);
        } finally {
          this.$store.commit('loader/setIsLoaderNeeded', false);
        }
      },

      async getExportFromServer(query) {
        const url = new URL(window.location.href);
        url.searchParams.set('pdf', 'true');

        const dateFrom = new Date(query.dateFrom);
        const dateTo = new Date(query.dateTo);
        const dateFromFormatted = dayjs(dateFrom).format('DD.MM.YY');
        const dateToFormatted = dayjs(dateTo).format('DD.MM.YY');

        const filename = `${this.pdfName.filename} ${dateFromFormatted}-${dateToFormatted}.pdf`;
        const title = `${this.pdfName.logname} ${this.$t('export.fromTo', {
          from: dateFromFormatted,
          to: dateToFormatted,
        })}`;
        await getPdf(url, filename, title, this.selectedComputers, this.selectedLogs);
      },
      async pdfExportFromHtml(isGrid, isAlbum) {
        const { query, params, name } = this.$route;
        const { log } = params;
        this.$store.commit('loader/setIsLoaderNeeded', true);
        let logname;
        let filename;
        if (name !== 'advanced' && name !== 'notifications') {
          const targetLog = logTypes.find((logType) => logType.slug === log);
          logname = targetLog.name;
          filename = log;
        } else if (name === 'advanced') {
          logname = this.$t('pages.advancedMode');
          filename = name;
        } else if (name === 'notifications') {
          logname = this.$t('pages.notifications');
          filename = name;
        }
        const dateFrom = new Date(query.dateFrom);
        const dateTo = new Date(query.dateTo);
        const dateFromFormatted = dayjs(dateFrom).format('DD.MM.YY');
        const dateToFormatted = dayjs(dateTo).format('DD.MM.YY');

        const target = document.getElementById('printable');
        const copy = target.cloneNode(true);
        const prtHtml = copy.innerHTML;

        let stylesHtml = '';
        [...document.querySelectorAll('link[rel="stylesheet"], style')].forEach((node) => {
          stylesHtml += node.outerHTML;
        });
        stylesHtml +=
          '<style type="text/css">html{width:21cm;height:29.7cm;}' +
          'body{display:block!important;}.view-grid_grid_8vvtA{grid-template-columns:auto!important;}</style>';

        const element = `<!DOCTYPE html>
                          <html>
                            <head>
                              ${stylesHtml}
                            </head>
                            <body>
                              <div style="margin:5px 5px 15px 5px;display:flex;justify-content:center">
                                Данные: ${logname} c: ${dateFromFormatted} по: ${dateToFormatted} </div>
                              ${prtHtml}
                            </body>
                          </html>`;

        const opt = {
          margin: 10,
          filename: `${filename} - ${query.dateFrom} - ${query.dateTo}.pdf`,
          pagebreak: { mode: ['avoid-all', 'css'] },
          image: { type: 'jpeg', quality: 1 },
          html2canvas: { scale: 4 },
        };
        if (isGrid) {
          let pagesRes = [];

          if (isAlbum) {
            opt.jsPDF = { orientation: 'landscape' };

            const pagesStart = element.split(/(?=<div tabindex="2")/);

            if (!pagesStart[1]) pagesRes = pagesStart;
            else {
              const pictures = pagesStart[1].split(/(?=<div tabindex)/);
              const pages = [];
              pages.push(pagesStart[0]);
              pagesRes = pages.concat(pictures);
            }
          } else {
            const pagesStart = element.split(/(?=<div tabindex="3")/);

            if (!pagesStart[1]) pagesRes = pagesStart;
            else {
              pagesRes.push(pagesStart[0]);
              const temp = document.createElement('div');
              temp.innerHTML = pagesStart[1];
              const pages = temp.children;

              for (let i = 0; i < pages.length; i += 2) {
                if (pages[i + 1]) pagesRes.push(pages[i].innerHTML + pages[i + 1].innerHTML);
                else pagesRes.push(pages[i].innerHTML);
              }
            }
          }

          let worker = html2pdf().set(opt).from(pagesRes[0]).toPdf();
          pagesRes.slice(1).forEach((page) => {
            worker = worker
              .get('pdf')
              .then((pdf) => {
                pdf.addPage();
              })
              .from(page)
              .toContainer()
              .toCanvas()
              .toPdf();
          });
          await worker.save();
        } else {
          await html2pdf().set(opt).from(element).save();
        }
        this.$store.commit('loader/setIsLoaderNeeded', false);
      },
      async exportTable(exportType) {
        const { query, params, name } = this.$route;
        const { view } = query;
        const { log } = params;

        const isServerExport = ROUTE_NAMES_SERVER_EXPORT_PDF.includes(name);
        const isPdf = exportType === FORMAT_EXPORT.PDF;
        this.$store.commit('loader/setIsLoaderNeeded', true);
        try {
          if (isPdf && isServerExport) {
            await this.getExportFromServer(query);
          } else if (isPdf && (log === 'messengers' || name === 'notifications')) {
            await this.pdfExportFromHtml(true, true);
          } else if (isPdf && (log !== 'screenshots' || (log === 'screenshots' && (!view || view !== 'grid')))) {
            await this.pdfExport(false, false);
          } else if (exportType === this.$t('pages.portraitOrientation')) await this.pdfExport(false);
          else if (exportType === this.$t('pages.landscapeOrientation')) await this.pdfExport(true);
          else {
            let table;
            if (
              (log && log === 'messengers') ||
              name === 'advanced' ||
              name === 'notifications' ||
              name === 'diagnostics'
            ) {
              const tableBody = document.getElementById('tableBody');
              const newTable = document.createElement('table');

              const newTableHead = document.createElement('thead');

              const newTableRow = document.createElement('tr');

              let columnsToExtract;
              if (log === 'messengers') {
                columnsToExtract = [
                  this.$t('pages.columns.time'),
                  this.$t('pages.columns.messenger'),
                  this.$t('pages.columns.buddy'),
                  this.$t('pages.columns.messageType'),
                  this.$t('pages.columns.messageTextOrFile'),
                  this.$t('pages.columns.username'),
                ];
              }
              if (name === 'diagnostics') {
                columnsToExtract = this.columns.map(({ title }) => title);
              }
              if (name === 'advanced') {
                // TODO: переписать на
                columnsToExtract = [
                  this.$t('pages.columns.eventTime'),
                  this.$t('pages.columns.user'),
                  this.$t('pages.columns.category'),
                  this.$t('pages.columns.details'),
                ];
              }
              if (name === 'notifications') {
                columnsToExtract = [
                  this.$t('pages.columns.agent'),
                  this.$t('pages.columns.eventTime'),
                  this.$t('pages.columns.reason'),
                  this.$t('pages.columns.category'),
                  this.$t('pages.columns.controller'),
                  this.$t('pages.columns.confirmationTime'),
                  this.$t('pages.columns.content'),
                  this.$t('pages.columns.keywords'),
                ];
              }

              columnsToExtract.forEach((columnName) => {
                const newTableHeaderCell = document.createElement('th');
                newTableHeaderCell.textContent = columnName;
                newTableRow.appendChild(newTableHeaderCell);
              });
              newTableHead.appendChild(newTableRow);
              const newTableBody = document.createElement('tbody');

              if (name === 'diagnostics') {
                // TODO: переписать xls по принципу построения pdf
                this.data.forEach((item) => {
                  const row = document.createElement('tr');
                  this.columns.forEach(({ field }) => {
                    const cell = document.createElement('td');
                    cell.textContent = this.fieldFunctions(item, field);
                    row.appendChild(cell);
                  });
                  newTableBody.appendChild(row);
                });
              } else {
                tableBody.childNodes.forEach((row) => {
                  newTableBody.appendChild(row.cloneNode(true));
                });
              }
              newTable.appendChild(newTableHead);
              newTable.appendChild(newTableBody);

              table = newTable;
            } else {
              const target = document.getElementById('printable');
              const copy = target.cloneNode(true);
              table = copy.getElementsByTagName('table')[0];
            }
            if (table) {
              const images = table.getElementsByTagName('img');

              for (let i = images.length; i-- > 0; ) {
                images[i].parentNode.removeChild(images[i]);
              }
              let firstName = name;
              if (name !== 'diagnostics' && name !== 'advanced' && name !== 'notifications') {
                firstName = log;
              }
              let filename = `${firstName} - ${query.dateFrom} - ${query.dateTo}`;
              if (exportType === 'xls') {
                const dataType = 'application/vnd.ms-excel';

                const tableHTML = table.outerHTML.replace(/ /g, '%20');

                filename += '.xls';

                const downloadLink = document.createElement('a');
                document.body.appendChild(downloadLink);

                if (navigator.msSaveOrOpenBlob) {
                  const blob = new Blob(['\ufeff', tableHTML], {
                    type: dataType,
                  });
                  navigator.msSaveOrOpenBlob(blob, filename);
                } else {
                  downloadLink.href = `data:${dataType}, ${tableHTML}`;
                  downloadLink.download = filename;
                  downloadLink.click();
                }
              } else {
                let content = null;
                let type = '';
                if (exportType === 'csv') {
                  filename += '.csv';

                  const csv = [];
                  const rows = table.querySelectorAll('table tr');

                  for (let i = 0; i < rows.length; i++) {
                    const row = [];
                    const cols = rows[i].querySelectorAll('td, th');

                    for (let j = 0; j < cols.length; j++) {
                      row.push(cols[j].innerText.replaceAll(',', ' '));
                    }

                    csv.push(row.join(','));
                  }

                  content = csv.join('\n');
                  type = 'text/csv';
                } /* else {
                  filename += '.rtf';
                  const html = table.outerHTML;
                  const rtfContent = htmlToRtf.convertHtmlToRtf(html);
                  const self = this;

                  // eslint-disable-next-line
                  content = rtfContent.replace(/[^\x00-\x7F]/g, (c) => `\\u${self.rftcharcode(c)}?`);
                  type = 'application/rtf'
                } */
                const file = new Blob([content], { type });
                const downloadLink = document.createElement('a');
                downloadLink.download = filename;
                downloadLink.href = window.URL.createObjectURL(file);
                downloadLink.style.display = 'none';
                document.body.appendChild(downloadLink);
                downloadLink.click();
              }
            }
          }
        } catch (err) {
          console.error(err);
        } finally {
          this.$store.commit('loader/setIsLoaderNeeded', false);
        }
      },
      rftcharcode(c) {
        const cc = c.charCodeAt();
        return cc <= 32767 ? cc : cc - 65535;
      },
    },
  };
</script>

<style lang="scss" module>
  .printButton {
    cursor: pointer;
    display: block;
    width: 120px;
    height: 35px;
    float: right;
    background: $blue;
    border: 2px solid $blue;
    border-radius: 2px;
    font: inherit;
    font-style: normal;
    font-weight: normal;
    font-size: 14px;
    text-align: center;
    color: $white;
    margin: 0px 5px;
    &:disabled {
      cursor: not-allowed;
      background: $branchColor;
      border: 2px solid $branchColor;
    }
  }

  .exportButton {
    background: white;
    color: #2985bf;
    &:disabled {
      cursor: not-allowed;
      color: $branchColor;
      border: 2px solid $branchColor;
      background: white;
    }
  }

  .dropdown {
    position: relative;
  }

  .dropdownContent {
    display: none;
    position: absolute;
    background-color: white;
    box-shadow: 2px 3px 5px rgba(0, 0, 0, 0.2);
    z-index: 50;
    top: 35px;
    width: 120px;
    font-size: 14px;
    left: 5px;
  }

  .dropdownNestedContent {
    display: none;
    position: absolute;
    background-color: white;
    box-shadow: 2px 3px 5px rgba(0, 0, 0, 0.2);
    z-index: 50;
    top: 35px;
    width: 120px;
    font-size: 14px;
    left: 5px;
  }

  .dropdownItem {
    color: black;
    padding: 12px 16px;
    text-decoration: none;
    display: block;
    cursor: pointer;
  }

  .dropdownNestedItem {
    color: black;
    padding: 12px 16px;
    text-decoration: none;
    display: block;
    cursor: pointer;
  }

  .item:hover {
    background-color: #eee;

    .dropdownNestedContent {
      display: block;
    }
  }

  .dropdown:hover .dropdownContent {
    display: block;
  }

  .dropdownNestedItem:hover {
    background-color: #eee;
  }
</style>
